<script setup lang="ts">
// The connection: the district's VectorSurv agency token, the agency its filings
// are attributed to, and a check that the credential works. The token is a
// secret — it travels in the request body and nowhere else, is never returned by
// the server, and is never logged.
import { computed, onMounted, reactive, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	VectorSurvAgencyOption,
	VectorSurvConfig,
	VectorSurvConfigRequest,
	VectorSurvTokenTestRequest,
	VectorSurvTokenTestResponse,
	VectorSurvVocabulary,
} from "@/type/vectorsurv";

const loading = ref(true);
const saving = ref(false);
const testing = ref(false);
const saved = ref(false);
const error = ref("");
const vocabularyError = ref("");
const vocabLoading = ref(false);
const tokenConfigured = ref(false);
// testedAgencies is the last test's answer: null until a test runs, so "the
// token is valid and sees no agency" is not the same screen as "nothing has
// been tested".
const testedAgencies = ref<VectorSurvAgencyOption[] | null>(null);
const testError = ref("");

const form = reactive({
	token: "",
	agencyId: null as number | null,
});

const vocabulary = reactive<VectorSurvVocabulary>({
	agencies: [],
});

// One option of a picker select: the VectorSurv id and the label a human reads.
// The id is what is saved; the label only comes from the vocabulary.
interface DefaultChoice {
	id: number;
	label: string;
}

// A saved id can outlive the vocabulary list it was chosen from — the row was
// removed upstream, or the vocabulary read failed. Keep a synthetic option for
// such an id so the stored value stays visible instead of silently reading as
// none.
function withSavedId(
	choices: DefaultChoice[],
	savedId: number | null,
): DefaultChoice[] {
	if (savedId === null || choices.some((choice) => choice.id === savedId)) {
		return choices;
	}
	return [...choices, { id: savedId, label: `Unknown (#${savedId})` }];
}

const agencyChoices = computed<DefaultChoice[]>(() =>
	withSavedId(
		vocabulary.agencies.map((agency) => ({
			id: agency.id,
			label: `${agency.name} (${agency.code})`,
		})),
		form.agencyId,
	),
);

// configuredAgency is the agency the stored connection files under, as the
// "already configured" messaging names it. It is the saved agencyId resolved
// against the credential's agencies — the same vocabulary the Agency picker
// offers, which is the credential's OWN agency, not the /v1/agency catalog. A
// null id is reported as unset rather than guessed at; an id the vocabulary does
// not carry (the row was removed upstream, or the vocabulary read failed) is
// named by its number so the stored value stays visible instead of reading as
// none.
//
// While the agencies are still being read it resolves to null — an id that is
// merely not yet in an empty list is not an unknown id — and the messaging below
// says it is resolving (configuringAgency) instead of mislabelling it.
const configuredAgency = computed<string | null>(() => {
	if (form.agencyId === null || vocabLoading.value) {
		return null;
	}
	const agency = vocabulary.agencies.find((a) => a.id === form.agencyId);
	if (agency) {
		return `${agency.name} (${agency.code})`;
	}
	return `#${form.agencyId}`;
});

// configuringAgency is the moment a stored agency is waiting on the read that
// names it, so the messaging says so rather than reporting no agency configured.
const configuringAgency = computed<boolean>(
	() => form.agencyId !== null && vocabLoading.value,
);

// applyConfig fills the form from a config read or write. The token is never
// part of the response, so the field always resets to empty — and an earlier
// test result describes a token that is no longer in that field, so it goes too.
function applyConfig(config: VectorSurvConfig): void {
	form.token = "";
	form.agencyId = config.agencyId;
	tokenConfigured.value = config.tokenConfigured;
	testedAgencies.value = null;
	testError.value = "";
}

async function loadVocabulary(): Promise<void> {
	vocabLoading.value = true;
	vocabularyError.value = "";
	try {
		const data = await apiClient.JSONGet<VectorSurvVocabulary>(
			"/api/integration/vectorsurv/vocabulary",
		);
		vocabulary.agencies = data.agencies;
	} catch (e: any) {
		vocabularyError.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load VectorSurv vocabulary.";
	} finally {
		vocabLoading.value = false;
	}
}

async function load(): Promise<void> {
	loading.value = true;
	error.value = "";
	try {
		const config = await apiClient.JSONGet<VectorSurvConfig>(
			"/api/integration/vectorsurv",
		);
		applyConfig(config);
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load VectorSurv configuration.";
	} finally {
		loading.value = false;
	}

	// The vocabulary needs the stored token, and its failure must not blank the
	// connection the config read already returned.
	if (tokenConfigured.value) {
		await loadVocabulary();
	}
}

// testToken proves the token in the field without saving anything, and shows
// the agencies it resolves to — the operator finds out which agency the
// credential belongs to before committing it. Test stores nothing, so a rejected
// token leaves the saved connection exactly as it was.
async function testToken(): Promise<void> {
	testing.value = true;
	testError.value = "";
	testedAgencies.value = null;
	try {
		const payload: VectorSurvTokenTestRequest = { token: form.token };
		const result = await apiClient.JSONPost<VectorSurvTokenTestResponse>(
			"/api/integration/vectorsurv/token/test",
			payload,
		);
		testedAgencies.value = result.agencies;
		// A token that resolves to exactly one agency has exactly one agency it
		// can file under, so select it rather than leave a field the operator
		// can forget to visit. The picker offers the credential's own agency, so
		// this can only ever select an agency the token can actually file under;
		// with more than one the choice is the operator's, and nothing is
		// guessed. Selecting here only changes the form — Save still persists.
		if (result.agencies.length === 1) {
			form.agencyId = result.agencies[0].id;
		}
	} catch (e: any) {
		testError.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to test the VectorSurv token.";
	} finally {
		testing.value = false;
	}
}

async function save(): Promise<void> {
	saving.value = true;
	error.value = "";
	saved.value = false;
	try {
		const payload: VectorSurvConfigRequest = {
			token: form.token,
			agencyId: form.agencyId,
		};
		const config = await apiClient.JSONPut<VectorSurvConfig>(
			"/api/integration/vectorsurv",
			payload,
		);
		applyConfig(config);
		saved.value = true;
		// A newly accepted token can change which agencies the credential lists,
		// so the vocabulary is re-read after a successful save.
		if (tokenConfigured.value) {
			await loadVocabulary();
		}
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to save VectorSurv configuration.";
	} finally {
		saving.value = false;
	}
}

onMounted(load);
</script>

<template>
	<div class="container py-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div class="d-flex align-items-center">
				<RouterLink
					class="btn btn-outline-secondary me-3"
					to="/_/configuration/integration/vectorsurv"
					title="Back to VectorSurv"
				>
					<i class="bi bi-arrow-left"></i>
				</RouterLink>
				<div>
					<h1 class="mb-0">VectorSurv connection</h1>
					<p class="text-muted mb-0">
						The agency token this district files with
					</p>
				</div>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div v-if="loading" class="text-center p-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<div v-else class="card mb-4 integration-card">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<div>
					<h2 class="h5 mb-0">Connection</h2>
				</div>
				<div class="d-flex gap-2">
					<ButtonLoading
						text="Test"
						icon="bi bi-check2-circle"
						:loading="testing"
						:disabled="loading || !form.token"
						@click="testToken"
					/>
					<ButtonLoading
						text="Save"
						icon="bi bi-save"
						:loading="saving"
						:disabled="loading"
						@click="save"
					/>
				</div>
			</div>
			<div class="card-body">
				<form @submit.prevent="save">
					<div class="row g-3">
						<div class="col-md-12">
							<label class="form-label" for="vectorsurv-token">
								API token
							</label>
							<input
								id="vectorsurv-token"
								v-model="form.token"
								type="password"
								class="form-control"
								:placeholder="
									tokenConfigured
										? 'Leave blank to keep the stored token'
										: 'Enter the agency token'
								"
								autocomplete="new-password"
							/>
							<div class="form-text">
								<template v-if="tokenConfigured">
									<span class="d-block">
										•••••••• — a token is currently stored; leave blank to keep
										it.
									</span>
									<span class="d-block">
										<template v-if="configuredAgency">
											Configured agency:
											<strong>{{ configuredAgency }}</strong
											>.
										</template>
										<template v-else-if="configuringAgency">
											Resolving the configured agency…
										</template>
										<template v-else>
											No agency is configured yet — choose one in the Agency
											field below to file under it.
										</template>
									</span>
								</template>
								<span v-else class="d-block">
									No token is currently stored.
								</span>
							</div>
						</div>

						<div class="col-md-6">
							<label class="form-label" for="vectorsurv-agency"> Agency </label>
							<select
								id="vectorsurv-agency"
								v-model.number="form.agencyId"
								class="form-select"
								:disabled="!tokenConfigured || vocabLoading"
							>
								<option :value="null">— none —</option>
								<option
									v-for="choice in agencyChoices"
									:key="choice.id"
									:value="choice.id"
								>
									{{ choice.label }}
								</option>
							</select>
							<div v-if="vocabularyError" class="text-danger small mt-1">
								{{ vocabularyError }}
							</div>
							<div v-else-if="vocabLoading" class="form-text">
								Loading the credential's agencies…
							</div>
							<div v-else class="form-text">
								The agency this district's filings are attributed to. Read from
								the stored token, never from VectorSurv's full agency catalog.
							</div>
						</div>
					</div>
				</form>

				<div v-if="testError" class="alert alert-danger mt-3 mb-0" role="alert">
					{{ testError }}
				</div>

				<div v-if="testedAgencies" class="alert alert-success mt-3">
					<div v-if="testedAgencies.length === 0" class="mb-0">
						The token is valid, but VectorSurv did not report an agency for it.
					</div>
					<template v-else>
						<p class="mb-2">The token resolves to:</p>
						<ul class="mb-0">
							<li v-for="agency in testedAgencies" :key="agency.id">
								{{ agency.name }} ({{ agency.code }})
								<span
									v-if="agency.id === form.agencyId"
									class="badge text-bg-primary ms-1"
								>
									configured agency
								</span>
							</li>
						</ul>
					</template>
				</div>

				<p class="text-muted small mt-3 mb-0">
					The token is stored encrypted at rest (AES-256-GCM under the
					deployment's key) and is never shown again once saved.
				</p>
			</div>
		</div>

		<ToastNotification
			v-model:show="saved"
			title="Saved"
			message="The VectorSurv connection was saved."
		/>
	</div>
</template>

<style scoped>
.integration-card {
	border-left: 5px solid #0d6efd;
}
</style>
